import React from 'react';
import { useTranslation } from 'react-i18next';
import { isNumber } from 'lodash';
import { IconWarning } from '../../../../../../assets/icons/icon-warning';
import { ControlWrapper } from '../../../../../../components/control-wrapper';
import { EmptyStateBox } from '../../../../../../components/empty-state-box';
import { InputSelect } from '../../../../../../components/input/select';
import { ChannelType } from '../../../../types';
import { CurrentAbsoluteValues } from './components/absolute-values';
import { EcoIndicator } from './components/eco-indicator';
import { EcoState } from './components/eco-state';
import { EnergyConsumption } from './components/energy-consumption';
import { EnergyConsumptionSummary } from './components/energy-consumption/summary';
import { useMeterCurrent } from './hooks/use-meter-current';

type MeterCurrentProps = {
  channel: ChannelType;
};

export const MeterCurrent: React.FC<MeterCurrentProps> = ({ channel }) => {
  const { t } = useTranslation('channel-details');
  const {
    data,
    selectedParameter,
    selectedSinglePhaseMeasurements,
    totalForwardActiveEnergy,
    selectedAverageValue,
    selectedMinValue,
    selectedMaxValue,
    isActivePowerSelected,
    isStandalone,
    parameters,
    supportedScalarAverage,
    supportedScalarMax,
    supportedScalarMin,
    ecoIndicator,
    changeSelectedParameter,
  } = useMeterCurrent({ channel });
  const ecoVisible = isNumber(ecoIndicator);
  const summaryVisible = supportedScalarMin || supportedScalarMax || supportedScalarAverage;

  if (data === null) {
    return (
      <div className="p-t-16 p-l-24 p-r-24">
        <EmptyStateBox content={t('noDataToDisplay')} icon={<IconWarning />} />
      </div>
    );
  }

  return (
    <>
      <ControlWrapper className="control-wrapper--column">
        <div className="max-width-desktop">
          <InputSelect
            options={parameters}
            value={selectedParameter}
            onChange={changeSelectedParameter}
            reverse
            sx={{ mb: 4 }}
            label={''}
          />
        </div>
        <EnergyConsumption
          maxValue={selectedMaxValue}
          averageValue={selectedAverageValue}
          selectedParameter={selectedParameter}
          changeSelectedParameter={changeSelectedParameter}
          selectedSinglePhaseMeasurements={selectedSinglePhaseMeasurements}
          isActivePowerSelected={isActivePowerSelected}
          totalForwardActiveEnergy={totalForwardActiveEnergy}
          isStandalone={isStandalone}
          ecoIndicator={ecoIndicator}
          {...(!isStandalone ? { itemPrefix: t('outputShort') } : {})}
        />
        {selectedSinglePhaseMeasurements?.length ? (
          <>
            <hr />
            <EnergyConsumptionSummary
              measurements={selectedSinglePhaseMeasurements}
              {...(!isStandalone ? { itemPrefix: t('outputShort') } : {})}
            />
          </>
        ) : null}
      </ControlWrapper>
      {ecoVisible && (
        <>
          <EcoIndicator value={ecoIndicator} />
          <EcoState value={ecoIndicator} />
        </>
      )}
      {summaryVisible && (
        <ControlWrapper className="control-wrapper control-wrapper--column control-wrapper--height-auto control-wrapper--stretch">
          <CurrentAbsoluteValues
            selectedParameter={selectedParameter}
            averageValue={selectedAverageValue}
            minValue={selectedMinValue}
            maxValue={selectedMaxValue}
            isActivePowerSelected={isActivePowerSelected}
            supportedScalarMin={supportedScalarMin}
            supportedScalarMax={supportedScalarMax}
            supportedScalarAverage={supportedScalarAverage}
          />
        </ControlWrapper>
      )}
    </>
  );
};
